import { useEffect, useRef } from 'react';

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const IS_LOCALHOST = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
export const DEV_BYPASS_TOKEN = 'TURNSTILE_LOCALHOST_BYPASS';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = resolve;
      s.onerror = () => { scriptPromise = null; reject(new Error('Turnstile script failed to load')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget.
 *   onToken(token)  – gaunamas žetonas; '' kai pasibaigia galiojimas / klaida
 *   resetRef        – (nebūtina) useRef(); resetRef.current() atstato widget'ą
 * Jei REACT_APP_TURNSTILE_SITE_KEY nesukonfigūruotas arba localhost – nieko nerodo.
 */
export default function Turnstile({ onToken, resetRef, theme = 'dark', style }) {
  const elRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (IS_LOCALHOST) {
      onTokenRef.current?.(DEV_BYPASS_TOKEN);
      if (resetRef) resetRef.current = () => onTokenRef.current?.(DEV_BYPASS_TOKEN);
      return;
    }
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;

    loadScript().then(() => {
      if (cancelled || !elRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(elRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme,
        callback: (token) => onTokenRef.current?.(token),
        'expired-callback': () => onTokenRef.current?.(''),
        'error-callback': () => onTokenRef.current?.(''),
      });
      if (resetRef) {
        resetRef.current = () => {
          try { window.turnstile.reset(widgetIdRef.current); } catch {}
          onTokenRef.current?.('');
        };
      }
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (resetRef) resetRef.current = null;
      if (widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [theme, resetRef]);

  if (IS_LOCALHOST || !TURNSTILE_SITE_KEY) return null;
  return <div ref={elRef} style={{ display: 'flex', justifyContent: 'center', margin: '12px 0', minHeight: 65, ...style }} />;
}
